/**
 * @fileoverview network-deployment-transport.ts — the browser-side
 *               WebSocketCommandPort implementation.
 * @module apps/network/global/io/hyperedges/network-deployment-transport
 *
 * Browser side of the wsbridge to `network.liquidefy.io`. Implements
 * the `DeploymentCommandPort` contract:
 *
 *   interface DeploymentCommandPort {
 *     dispatch(op, payload?): Promise<unknown>;
 *     subscribe(listener: (e: DeploymentStreamEvent) => void): () => void;
 *   }
 *
 * Wire format (`network/servers/deployment/0.1.0/src/transport/ws.ts`
 * is the server-side mirror):
 *
 *   Browser → Server:
 *     { kind: "dispatch", id, op, payload? }
 *     { kind: "ping" }
 *
 *   Server → Browser:
 *     { kind: "hello", version }
 *     { kind: "ack", id }
 *     { kind: "result", id, result }
 *     { kind: "error", id?, error: { code, message } }
 *     { kind: "event", event: DeploymentStreamEvent }
 *
 * Reconnect behavior: exponential backoff (250ms → 8s cap),
 * with auth retry on every connect via the supplied `tokenSource`.
 */

// Import the transport contract from the CANONICAL `.ts` source, not via
// the `DeploymentIO.svelte` re-export: plain `tsc` resolves `*.svelte`
// through the ambient default-only module decl, so named *type* imports
// from a `.svelte` file don't type-check (svelte-check tolerates it; tsc
// does not). `deployment-io.ts` is the single source these types live in.
import type {
  DeploymentCommandPort,
  DeploymentStreamEvent,
} from "../nodes/deployment/deployment-io.js";

export interface CreateWebSocketCommandPortInput {
  readonly endpoint: string;
  readonly tokenSource?: () => Promise<string | null>;
  readonly onConnect?: () => void;
  readonly onDisconnect?: () => void;
  readonly onError?: (err: Error) => void;
}

export interface WebSocketCommandPort extends DeploymentCommandPort {
  /** Force a reconnect (e.g. after auth token rotation). */
  reconnect(): void;
  /** Close + tear down — called by the component's $effect cleanup. */
  close(): void;
}

interface PendingDispatch {
  resolve: (v: unknown) => void;
  reject: (e: Error) => void;
}

/** A dispatch issued before the socket was OPEN. Parked here and flushed on
 *  connect (rather than rejected) — the contract the rest of the app already
 *  assumes ("methods queue in the pre-open window"; see IO.svelte). Each
 *  carries a bounded timer so a never-connecting socket fails the caller
 *  cleanly instead of hanging forever. */
interface QueuedDispatch {
  op: string;
  payload?: unknown;
  resolve: (v: unknown) => void;
  reject: (e: Error) => void;
  timer: ReturnType<typeof setTimeout>;
}

export function createWebSocketCommandPort(
  input: CreateWebSocketCommandPortInput,
): WebSocketCommandPort {
  const {
    endpoint,
    tokenSource = async () => null,
    onConnect,
    onDisconnect,
    onError,
  } = input;

  let ws: WebSocket | null = null;
  let nextDispatchId = 1;
  const pending = new Map<string, PendingDispatch>();
  const subscribers = new Set<(e: DeploymentStreamEvent) => void>();
  let reconnectAttempt = 0;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let closed = false;
  // Dispatches issued while the socket is still CONNECTING (or mid-reconnect)
  // are parked here and flushed on open — instead of being rejected. This is
  // the behavior IO.svelte's comment already promises ("methods queue in the
  // pre-open window"); without it, any dispatch that loses the connect race
  // fails hard with "transport not connected" and never retries.
  const sendQueue: QueuedDispatch[] = [];
  // Guards the async window inside connect() (the tokenSource await), so that
  // several queued dispatches each calling connect() can't open >1 socket.
  let connecting = false;
  // How long a queued dispatch waits for the socket before giving up.
  const CONNECT_WAIT_MS = 12_000;

  function backoffMs(): number {
    const base = Math.min(8000, 250 * 2 ** reconnectAttempt);
    // Jitter up to 25%
    return base + Math.random() * base * 0.25;
  }

  async function connect(): Promise<void> {
    if (closed) return;
    if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
      return;
    }
    if (connecting) return; // another connect() is already inside its async window

    connecting = true;
    let token: string | null;
    try {
      token = await tokenSource();
    } finally {
      connecting = false;
    }
    // Re-check after the await — state may have changed while we waited
    // (closed, or another path already opened the socket).
    if (closed) return;
    if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
      return;
    }
    if (!token) {
      // No token yet — sit idle. A 30s "check for token" loop runs
      // so we pick up a freshly-written JWT after the user signs in.
      // (No socket is opened; no server-side handshake attempt.)
      scheduleTokenWait();
      return;
    }
    const url = `${endpoint}?t=${encodeURIComponent(token)}`;

    try {
      ws = new WebSocket(url);
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      onError?.(err);
      scheduleReconnect();
      return;
    }

    ws.onopen = () => {
      reconnectAttempt = 0;
      onConnect?.();
      flushQueue();
    };

    ws.onmessage = (evt) => {
      let frame: unknown;
      try {
        frame = JSON.parse(String(evt.data));
      } catch {
        return;
      }
      handleFrame(frame);
    };

    ws.onerror = (evt) => {
      const err = new Error(
        `WebSocket error: ${(evt as ErrorEvent).message ?? "unknown"}`,
      );
      onError?.(err);
    };

    ws.onclose = (evt) => {
      onDisconnect?.();
      // Reject every in-flight dispatch — they'll retry via the
      // edge-tier action methods if appropriate.
      for (const [, p] of pending) {
        p.reject(new Error("transport closed"));
      }
      pending.clear();

      // 1008 = policy violation. Server-side this means auth_invalid.
      // The token is stale — clear it and wait for a fresh one
      // (don't tight-loop reconnect with the same bad token).
      if (evt.code === 1008) {
        try { localStorage?.removeItem("network:consumer:auth"); } catch { /**/ }
        onError?.(new Error("auth invalid — please sign in again"));
        // Auth is dead until re-sign-in — don't leave queued dispatches
        // hanging for the full connect timeout.
        drainQueue(new Error("auth invalid — please sign in again"));
        if (!closed) scheduleTokenWait();
        return;
      }
      if (!closed) scheduleReconnect();
    };
  }

  function scheduleReconnect(): void {
    if (closed || reconnectTimer) return;
    const delay = backoffMs();
    reconnectAttempt += 1;
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null;
      void connect();
    }, delay);
  }

  /** Idle-poll for a token to appear in localStorage. Used when
   *  tokenSource() returns null (e.g. user hasn't signed in yet)
   *  and when the server rejected a stale token. */
  function scheduleTokenWait(): void {
    if (closed || reconnectTimer) return;
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null;
      void connect();
    }, 2000); // poll every 2s — cheap; no socket open until token shows up
  }

  /** Send a dispatch on the (open) socket and track it for result correlation. */
  function sendNow(
    op: string,
    payload: unknown,
    resolve: (v: unknown) => void,
    reject: (e: Error) => void,
  ): void {
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      reject(new Error("transport not connected"));
      return;
    }
    const id = String(nextDispatchId++);
    pending.set(id, { resolve, reject });
    ws.send(JSON.stringify({ kind: "dispatch", id, op, payload }));
  }

  /** Flush every queued dispatch onto a freshly-open socket. */
  function flushQueue(): void {
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    const items = sendQueue.splice(0, sendQueue.length);
    for (const q of items) {
      clearTimeout(q.timer);
      sendNow(q.op, q.payload, q.resolve, q.reject);
    }
  }

  /** Reject + clear the whole queue (teardown, or an unrecoverable auth close). */
  function drainQueue(err: Error): void {
    const items = sendQueue.splice(0, sendQueue.length);
    for (const q of items) {
      clearTimeout(q.timer);
      q.reject(err);
    }
  }

  function handleFrame(frame: unknown): void {
    if (!isObject(frame) || typeof frame["kind"] !== "string") return;
    const kind = frame["kind"];

    if (kind === "event" && isObject(frame["event"])) {
      const event = frame["event"] as unknown as DeploymentStreamEvent;
      for (const listener of subscribers) listener(event);
      return;
    }

    if (kind === "result" && typeof frame["id"] === "string") {
      const id = frame["id"];
      const p = pending.get(id);
      if (p) {
        pending.delete(id);
        p.resolve(frame["result"]);
      }
      return;
    }

    if (kind === "error" && typeof frame["id"] === "string") {
      const id = frame["id"];
      const p = pending.get(id);
      if (p) {
        pending.delete(id);
        const errObj = isObject(frame["error"]) ? frame["error"] : null;
        const code = (errObj && typeof errObj["code"] === "string") ? errObj["code"] : "DISPATCH_ERROR";
        const message = (errObj && typeof errObj["message"] === "string") ? errObj["message"] : "dispatch failed";
        const err = new Error(message);
        (err as Error & { code?: string }).code = code;
        p.reject(err);
      }
      return;
    }

    if (kind === "hello") {
      // Server greeted us. No-op — the open callback already fired.
      return;
    }
  }

  // Kick off the initial connect.
  void connect();

  return {
    dispatch(op, payload) {
      return new Promise<unknown>((resolve, reject) => {
        if (closed) {
          reject(new Error("transport closed"));
          return;
        }
        // Socket open → send immediately.
        if (ws && ws.readyState === WebSocket.OPEN) {
          sendNow(op, payload, resolve, reject);
          return;
        }
        // Socket still connecting (initial mount race) or mid-reconnect →
        // queue and flush on open, rather than rejecting. A bounded timer
        // keeps a never-connecting socket from hanging the caller forever.
        const q: QueuedDispatch = {
          op,
          payload,
          resolve,
          reject,
          timer: setTimeout(() => {
            const i = sendQueue.indexOf(q);
            if (i >= 0) sendQueue.splice(i, 1);
            reject(new Error("transport not connected (timed out connecting)"));
          }, CONNECT_WAIT_MS),
        };
        sendQueue.push(q);
        // Make sure a connect attempt is in flight (idempotent).
        void connect();
      });
    },

    subscribe(listener) {
      subscribers.add(listener);
      return () => {
        subscribers.delete(listener);
      };
    },

    reconnect() {
      if (ws) {
        ws.close();
        ws = null;
      }
      reconnectAttempt = 0;
      void connect();
    },

    close() {
      closed = true;
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      if (ws) {
        ws.close();
        ws = null;
      }
      drainQueue(new Error("transport closed"));
      pending.clear();
      subscribers.clear();
    },
  };
}

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}
